import {DateTime} from "luxon";

const toNumericOrNull = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const normalizeStringValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
};

const normalizeComparableValue = (value) => normalizeStringValue(value).toLowerCase();

export const normalizeRequestEventDate = (value) => {
    if (!value) {
        return "";
    }
    let parsed = DateTime.fromSQL(value);
    if (!parsed.isValid) {
        parsed = DateTime.fromISO(value);
    }
    if (!parsed.isValid) {
        parsed = DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss");
    }
    if (!parsed.isValid) {
        return "";
    }
    return parsed.toFormat("yyyy-MM-dd HH:mm:ss");
};

const extractEventUserCandidate = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return null;
    }
    const candidates = [
        eventItem.user,
        eventItem.creator,
        eventItem.created_by,
        eventItem.createdBy,
        eventItem.requested_by,
        eventItem.requestedBy,
    ];
    const header = eventItem.document_header ?? eventItem.documentHeader ?? null;
    if (header) {
        candidates.push(header.creator);
        if (Array.isArray(header.users) && header.users.length > 0) {
            candidates.push(header.users[0]);
        }
    }
    return candidates.find((candidate) => candidate && typeof candidate === "object") ?? null;
};

export const normalizeRequestEventUser = (eventItem) => {
    const candidate = extractEventUserCandidate(eventItem);
    if (!candidate) {
        return null;
    }
    const firstName = candidate.firstname ?? candidate.first_name ?? candidate.firstName ?? "";
    const lastName = candidate.lastname ?? candidate.last_name ?? candidate.lastName ?? "";
    const fullname = candidate.fullname
        ?? [firstName, lastName].filter(Boolean).join(" ")
        ?? candidate.name
        ?? candidate.username
        ?? candidate.email
        ?? "";
    return {
        id: candidate.id ?? candidate.uuid ?? null,
        fullname,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        firstname: firstName || undefined,
        lastname: lastName || undefined,
        color: candidate.color ?? null,
        picture: candidate.picture ?? null,
    };
};

export const resolveRequestEventQuantity = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return 0;
    }
    const candidate = toNumericOrNull(
        eventItem.quantity
        ?? eventItem.confirmed_quantity
        ?? eventItem.qty
        ?? eventItem.value
    );
    return candidate ?? 0;
};

export const resolveRequestEventBodyId = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return null;
    }
    const candidates = [
        eventItem.bodyId,
        eventItem.body_id,
        eventItem.document_body_id,
        eventItem.documentBodyId,
        eventItem.document_body?.id,
        eventItem.document_body?.body_id,
        eventItem.documentBody?.id,
        eventItem.documentBody?.body_id,
        eventItem.body?.id,
    ];
    const match = candidates.find((value) => value !== null && value !== undefined && normalizeStringValue(value));
    return match ?? null;
};

export const resolveRequestEventProductIdentifiers = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return {productId: null, productCode: ""};
    }
    const product = eventItem.product
        ?? eventItem.document_product
        ?? eventItem.documentProduct
        ?? eventItem.document_body?.product
        ?? eventItem.documentBody?.product
        ?? {};
    const productId = product.id
        ?? product.product_id
        ?? eventItem.product_id
        ?? eventItem.productId
        ?? eventItem.document_body?.product_id
        ?? eventItem.documentBody?.product_id
        ?? null;
    const codeCandidates = [
        product.code,
        product.product_code,
        product.product_reference,
        eventItem.product_code,
        eventItem.product_reference,
        eventItem.productCode,
    ];
    const productCode = codeCandidates.find((value) => typeof value === "string" && value.trim()) ?? "";
    return {
        productId,
        productCode: productCode.trim(),
    };
};

const resolveRequestEventProduct = (eventItem) => {
    const {productId, productCode} = resolveRequestEventProductIdentifiers(eventItem);
    const product = eventItem.product
        ?? eventItem.document_product
        ?? eventItem.documentProduct
        ?? eventItem.document_body?.product
        ?? eventItem.documentBody?.product
        ?? {};
    const nameCandidates = [
        product.name,
        product.product_name,
        eventItem.product_name,
        eventItem.document_body?.product_name,
        eventItem.documentBody?.product_name,
    ];
    const name = nameCandidates.find((value) => typeof value === "string" && value.trim()) ?? "";
    return {
        id: productId ?? null,
        code: productCode || null,
        name: name.trim(),
    };
};

const resolveRequestEventUnitSymbol = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return "";
    }
    const candidates = [
        eventItem.unit_symbol,
        eventItem.unitSymbol,
        eventItem.unit?.symbol,
        eventItem.unit_value?.symbol,
    ];
    return candidates.find((value) => typeof value === "string" && value.trim()) ?? "";
};

const resolveEventType = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return "";
    }
    const raw = eventItem.type ?? eventItem.event_type ?? eventItem.eventType ?? "";
    if (typeof raw !== "string") {
        return "";
    }
    const normalized = raw.toUpperCase();
    if (normalized === "CONFIRMED_QUANTITY") {
        return "CONFIRMED_QTY";
    }
    return normalized;
};

export const sanitizeRequestEvent = (eventItem) => {
    if (!eventItem || typeof eventItem !== "object") {
        return null;
    }
    const id = eventItem.id
        ?? eventItem.event_id
        ?? eventItem.eventId
        ?? eventItem.uuid
        ?? null;
    const statusCandidate = eventItem.status ?? eventItem.state ?? "";
    const status = typeof statusCandidate === "string"
        ? statusCandidate
        : "";
    return {
        id,
        type: resolveEventType(eventItem),
        status,
        quantity: resolveRequestEventQuantity(eventItem),
        unitSymbol: resolveRequestEventUnitSymbol(eventItem),
        eventDate: normalizeRequestEventDate(
            eventItem.event_date
            ?? eventItem.eventDate
            ?? eventItem.created_at
            ?? eventItem.createdAt
            ?? null
        ),
        user: normalizeRequestEventUser(eventItem),
        product: resolveRequestEventProduct(eventItem),
        rawEvent: eventItem,
        bodyId: resolveRequestEventBodyId(eventItem),
        closed: typeof eventItem.closed === "boolean" ? eventItem.closed : null,
    };
};

export const extractRequestEvents = (payload) => {
    if (!payload) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload.events)) {
        return payload.events;
    }
    if (Array.isArray(payload.data)) {
        return payload.data;
    }
    if (Array.isArray(payload.items)) {
        return payload.items;
    }
    if (Array.isArray(payload.rows)) {
        return payload.rows;
    }
    return [];
};

export const isPendingRequestEvent = (eventItem) => {
    if (!eventItem) {
        return true;
    }
    const status = typeof eventItem.status === "string"
        ? eventItem.status
        : (typeof eventItem.state === "string" ? eventItem.state : "");
    const normalized = status.trim().toLowerCase();
    if (!normalized) {
        return true;
    }
    return normalized === "pending" || normalized === "waiting";
};

export const normalizeComparableId = normalizeComparableValue;
